import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Download, RefreshCw, Share2, Upload } from 'lucide-react';
import { FILTERS, renderSelfie } from '../lib/selfie';
import { loadBitmap, saveOrShare } from '../lib/photos';
import { api, errorMessage } from '../lib/api';
import { cn } from '../lib/utils';
import SelfieWall from './SelfieWall';

/* Take a selfie, pick a filter, then share it or post it on the selfie wall
   for the whole team. The filters run on the device; only a posted picture
   leaves it. */
export default function SelfieStudio() {
  const inputRef = useRef(null);
  const [bitmap, setBitmap] = useState(null);
  const [filter, setFilter] = useState(FILTERS[0].key);
  const [preview, setPreview] = useState(null); // { url, blob }
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const [posted, setPosted] = useState(null); // the preview url already on the wall
  const [wall, setWall] = useState(null);

  const loadWall = useCallback(async () => {
    try {
      setWall((await api.selfies(60)).selfies);
    } catch {
      setWall([]);
    }
  }, []);
  useEffect(() => {
    loadWall();
  }, [loadWall]);

  const post = async () => {
    if (!preview) return;
    setBusy(true);
    setDone('');
    try {
      const form = new FormData();
      form.append('photo', preview.blob, `selfie-${filter}.jpg`);
      form.append('filter', filter);
      await api.postSelfie(form);
      setPosted(preview.url);
      setDone('On the wall. Everyone can see it below.');
      loadWall();
    } catch (err) {
      setDone(errorMessage(err, 'Could not post it'));
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      setBitmap(await loadBitmap(file));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!bitmap) return undefined;
    let live = true;
    setBusy(true);
    (async () => {
      const canvas = await renderSelfie(bitmap, filter);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
      if (!live) return;
      setPreview((p) => {
        if (p?.url) URL.revokeObjectURL(p.url);
        return { url: URL.createObjectURL(blob), blob };
      });
      setBusy(false);
    })();
    return () => {
      live = false;
    };
  }, [bitmap, filter]);

  const share = async () => {
    if (!preview) return;
    const name = `inny-${filter}-${new Date().toISOString().slice(0, 10)}.jpg`;
    const how = await saveOrShare(preview.blob, name, 'Inny selfie');
    setDone(how === 'shared' ? 'Shared.' : how === 'saved' ? 'Saved to the device.' : '');
  };

  const current = FILTERS.find((f) => f.key === filter);
  return (
    <section className="mt-3">
      <input ref={inputRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onFile} />
      {!bitmap ? (
        <div className="rounded-xl bg-surface px-4 py-8 text-center">
          <p className="font-cond text-[24px] font-bold leading-none">Warehouse photo booth</p>
          <p className="mt-2 text-[14px] text-ink2">Eight filters, all of them warehouse-approved. Nothing leaves the scanner unless you share it or post it on the wall.</p>
          <button type="button" onClick={() => inputRef.current?.click()} className="mt-5 inline-flex h-14 items-center gap-2 rounded-xl bg-ink px-6 text-[17px] font-semibold text-bg active:opacity-80">
            <Camera className="h-5 w-5" /> Take a selfie
          </button>
        </div>
      ) : (
        <>
          <div className="relative overflow-hidden rounded-xl bg-surface">
            {preview && <img src={preview.url} alt="Your selfie with the filter" className="block w-full" />}
            {busy && <div className="absolute inset-0 flex items-center justify-center bg-bg/60 text-[14px] font-semibold">Developing…</div>}
          </div>
          <p className="mt-2 px-1 text-[13px] text-ink2">{current?.hint}</p>
          <div className="-mx-3 mt-2 flex gap-1.5 overflow-x-auto px-3 pb-1">
            {FILTERS.map((f) => (
              <button key={f.key} type="button" onClick={() => setFilter(f.key)} aria-pressed={filter === f.key} className={cn('h-10 shrink-0 rounded-full px-3 text-[13px] font-semibold', filter === f.key ? 'bg-ink text-bg' : 'bg-surface text-ink2 active:bg-sunken')}>
                {f.name}
              </button>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} className="flex h-12 items-center justify-center gap-2 rounded-xl bg-surface px-4 text-[14px] font-semibold active:bg-sunken">
              <RefreshCw className="h-4 w-4" /> Retake
            </button>
            <button type="button" onClick={share} disabled={!preview || busy} aria-label={navigator.canShare ? 'Share' : 'Save'} className="flex h-12 w-12 items-center justify-center rounded-xl bg-surface active:bg-sunken disabled:opacity-50">
              {navigator.canShare ? <Share2 className="h-5 w-5" /> : <Download className="h-5 w-5" />}
            </button>
            <button type="button" onClick={post} disabled={!preview || busy || posted === preview?.url} className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-ink text-[15px] font-semibold text-bg active:opacity-80 disabled:opacity-50">
              <Upload className="h-5 w-5" /> {posted && posted === preview?.url ? 'On the wall' : 'Post on the wall'}
            </button>
          </div>
          {done && <p className="mt-2 text-[13px] text-ink2">{done}</p>}
        </>
      )}
      <SelfieWall selfies={wall} onChange={loadWall} />
    </section>
  );
}
